/**
 * «Armá tu nave» (encargo 117): qué capítulo (Antena) y qué escena (Tren de aterrizaje) le toca a cada
 * alumno. Emmanuel pidió que salgan del onboarding y del nivel, no fijos: el dato del onboarding es
 * «¿Para qué querés el inglés?» (`user_profiles.learning_goals`, hasta dos: work, travel, studies,
 * relocation, growth, culture). Puro: recibe el catálogo y devuelve el id.
 */
import type { CefrLevel } from '../../../lib/englishApi.js';

/** Libros por motivo, en orden de preferencia (catálogo de /listen). */
const BOOKS_BY_GOAL: Record<string, readonly string[]> = {
  growth: ['atomic-habits', 'what-if'],
  culture: ['maradona', 'indio-solari', 'the-night-shift'],
  work: ['netflix', 'impossible-interviews', 'atomic-habits'],
  travel: ['gringo-mode', 'maradona'],
  relocation: ['gringo-mode', 'how-english-got-weird'],
  studies: ['how-english-got-weird', 'what-if'],
};
/** Sin motivo (o sin capítulo en su nivel): el de la tabla aprobada. */
const DEFAULT_BOOK = 'atomic-habits';

/** Escenas por motivo (catálogo curado de escenas), en orden de preferencia. */
const SCENES_BY_GOAL: Record<string, readonly string[]> = {
  work: ['work-networking', 'work-interview', 'work-presentation', 'work-raise'],
  travel: [
    'travel-cafe',
    'travel-directions',
    'travel-airport',
    'travel-hotel',
    'travel-immigration',
  ],
  relocation: [
    'daily-gym-signup',
    'life-doctor',
    'life-return',
    'travel-immigration',
    'life-support',
  ],
  studies: ['social-first-class', 'social-smalltalk', 'work-presentation'],
  growth: ['social-first-class', 'social-smalltalk', 'life-support'],
  culture: ['social-movie-night', 'social-smalltalk', 'social-first-date'],
};

const INTENSITY_ORDER = ['cozy', 'natural', 'push', 'stretch', 'native'] as const;
/**
 * La intensidad que le queda cómoda a cada nivel. A0 como A1 (card 599: sin entrada, el Tren de un
 * A0 abría la lista de escenas); la escena de un A0 corre igual en `starter` (`ScenarioBriefing`).
 * Tipada por `CefrLevel`: un nivel nuevo sin entrada no compila.
 */
export const INTENSITY_FOR_CEFR: Readonly<Record<CefrLevel, number>> = {
  A0: 0,
  A1: 0,
  A2: 0,
  B1: 1,
  B2: 2,
  C1: 3,
  C2: 3,
};
/** Hasta este nivel, sin motivo, el café; de ahí arriba, el aeropuerto. */
const CAFE_LEVELS = new Set(['A0', 'A1', 'A2']);

export interface BookChoice {
  slug: string;
  /** Niveles con narración del capítulo 1 (vacío = no se encontró el capítulo). */
  firstChapterLevels: readonly string[];
}

/** El libro de la Antena: el primero de sus motivos con capítulo 1 en su nivel. */
export function pickBook(
  goals: readonly string[],
  cefr: string,
  books: readonly BookChoice[],
): string {
  const available = (slug: string) =>
    books.some((b) => b.slug === slug && b.firstChapterLevels.includes(cefr));
  for (const g of goals) {
    const hit = (BOOKS_BY_GOAL[g] ?? []).find(available);
    if (hit) return hit;
  }
  return DEFAULT_BOOK;
}

export interface SceneChoice {
  id: string;
  suggestedIntensity: string;
}

/**
 * La escena del Tren: el motivo elige el tipo; el nivel, cuál (la intensidad sugerida más cerca de
 * la cómoda para su nivel). Sin motivo, decide el nivel: el café en A0-A2, el aeropuerto de B1 arriba.
 */
export function pickScene(
  goals: readonly string[],
  cefr: string,
  scenes: readonly SceneChoice[],
): string {
  const target = (INTENSITY_FOR_CEFR as Readonly<Record<string, number | undefined>>)[cefr];
  if (target === undefined) throw new Error(`ship: nivel sin escena para el Tren: ${cefr}`);
  const level = (s: SceneChoice) => {
    const i = INTENSITY_ORDER.indexOf(s.suggestedIntensity as (typeof INTENSITY_ORDER)[number]);
    return i < 0 ? 1 : i;
  };
  for (const g of goals) {
    const candidates = (SCENES_BY_GOAL[g] ?? [])
      .map((id) => scenes.find((s) => s.id === id))
      .filter((s): s is SceneChoice => s !== undefined);
    if (candidates.length === 0) continue;
    // La más cercana a su nivel; ante empate, la primera de la lista (la más representativa).
    let best = candidates[0]!;
    for (const s of candidates) {
      if (Math.abs(level(s) - target) < Math.abs(level(best) - target)) best = s;
    }
    return best.id;
  }
  return CAFE_LEVELS.has(cefr) ? 'travel-cafe' : 'travel-airport';
}
